import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { classic } from '~/themes';

type IProps = {
  textColor?: string;
  color: 'primary' | 'secondary';
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  disabled?: boolean;
  hidden?: boolean;
  type?: 'button' | 'submit' | 'reset';
  opaque?: boolean;
  width?: string;
  minWidth?: string;
  withoutIconMargin?: boolean;
  textTransform?: string;

  margin?: string;
  position: 'left' | 'right';
  onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const ButtonSide = forwardRef<HTMLButtonElement, IProps>(
  (
    {
      color,
      textColor,
      onClick,
      children,
      disabled,
      hidden,
      type,
      opaque,
      width,
      minWidth,
      withoutIconMargin,
      textTransform,
      margin,
      position,
      onMouseEnter,
      onMouseLeave,
    },
    ref
  ) => (
    <Container
      disabled={disabled}
      color={color}
      hidden={hidden}
      minWidth={minWidth}
      onClick={onClick}
      opaque={opaque}
      ref={ref}
      textColor={textColor}
      textTransform={textTransform}
      type={type}
      width={width}
      withoutIconMargin={withoutIconMargin}
      $margin={margin}
      $position={position}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </Container>
  )
);

export default ButtonSide;

const Container = styled.button<{
  disabled?: boolean;
  hidden?: boolean;
  textColor?: string | 'white';
  color: 'primary' | 'secondary';
  opaque?: boolean;
  width?: string;
  minWidth?: string;
  withoutIconMargin?: boolean;
  textTransform?: string;
  $margin?: string;
  $position: 'left' | 'right';
}>`
  align-items: center;
  border-radius: ${({ $position }) => {
    if ($position === 'left') return '0 12px 12px 0';
    else return '12px 0 0 12px';
  }};

  box-shadow:
    inset 0 2px 2px 0 rgba(255, 255, 255, 0.35),
    inset 0 -2px 2px 0 rgba(0, 0, 0, 0.4),
    0 3px 4px 0 rgba(0, 0, 0, 0.25);
  background-color: ${({ color }) => {
    if (color === 'primary') return `${classic.token?.colorPrimary}`;
    return `${classic.token?.colorTextSecondary}`;
  }};
  color: ${classic.token?.colorWhite};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  min-height: 42px;
  min-width: ${({ minWidth }) => minWidth ?? '0'};
  font-size: 1.25rem;
  font-weight: 700;
  justify-content: center;
  letter-spacing: normal;
  margin: ${({ $margin }) => ($margin ? $margin : '0')};
  opacity: ${({ disabled, hidden }) => {
    if (hidden) return 0;
    if (disabled) return 0.5;

    return 1;
  }};
  padding: 8px 30px;
  pointer-events: ${({ disabled, hidden }) =>
    disabled ?? hidden ? 'none' : 'auto'};
  text-transform: ${({ textTransform }) => textTransform ?? 'uppercase'};

  width: ${({ width }) => width ?? '200px'};
  @media screen and (max-height: 415px) {
    height: 30px;
    font-size: 1rem;
  }
  @media screen and (max-height: 900px) {
    font-size: 1.15rem;
  }
  @media screen and (max-width: 700px) {
    font-size: 0.95rem;
  }
  @media screen and (max-height: 700px) {
    font-size: 0.95rem;
  }
  @media screen and (max-width: 415px) {
    font-size: 1rem;
    padding: 8px 10px;
    width: ${({ width }) => width ?? '160px'};
  }

  @media screen and (max-height: 415px) {
    font-size: 1rem;
  }

  @media screen and (max-width: 667px) {
    padding: 8px 10px;
    width: ${({ width }) => width ?? '160px'};
  }
  @media screen and (max-height: 600px) {
    padding: 8px 10px;
    width: ${({ width }) => width ?? '160px'};
  }

  &:focus {
    font-weight: bold;
    outline: 0;
  }

  &:hover {
    background-color: ${classic.token?.colorPrimaryBgHover};
  }
`;

//  transition: all 0.3s linear;
